import { createTheme, type Shadows, type Theme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import { MuiDataGrid } from './MuiDataGrid';

const baseTheme = createTheme();

const mainFont = 'var(--main-font-family, "Geist", -apple-system, "system-ui", "Trebuchet MS", Roboto, Ubuntu, sans-serif)';
/** Literal family list for contexts that cannot resolve CSS variables (canvas `ctx.font`). */
export const NUMERIC_FONT_FAMILY = '"Geist", -apple-system, "system-ui", "Trebuchet MS", Roboto, Ubuntu, sans-serif';
const numericsFont = `var(--numeric-font-family, ${NUMERIC_FONT_FAMILY})`;
const body1 = {
  fontSize: '0.875rem',
  fontFamily: mainFont,
  lineHeight: '143%',
  letterSpacing: '0.2px',
};
const body2 = {
  fontSize: '0.75rem',
  fontFamily: mainFont,
  lineHeight: '133%',
  letterSpacing: '0.2px',
  fontFeatureSettings: '"liga" 0, "calt" 0',
};
const customElevationShadowInsets =
  '0px -1px 2px 0px rgba(0, 0, 0, 0.16) inset, 0px 1px 3px 0px rgba(255, 255, 255, 0.12) inset,';
const createCustomShadows = (baseTheme: Theme, insetShadow: string): Shadows => {
  return [baseTheme.shadows[0], ...baseTheme.shadows.slice(1).map((shadow) => insetShadow + shadow)] as Shadows;
};

export const createGradientBorderPseudo = (gradient: string) => ({
  content: '""',
  position: 'absolute' as const,
  inset: 0,
  borderRadius: 'inherit',
  padding: '1px',
  background: gradient,
  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
  WebkitMaskComposite: 'xor',
  maskComposite: 'exclude',
  pointerEvents: 'none' as const,
});

export const createGradientBorder = (startAt = '50%', endAt = '100%', opacity = 0.12, mode: 'light' | 'dark') => {
  if (mode === 'dark') {
    return `linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0) ${startAt}, rgba(220,220,220,${opacity}) ${endAt})`;
  } else {
    return `linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0) ${startAt}, rgba(0,0,0,${opacity}) ${endAt})`;
  }
};

export const muiTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 840,
      lg: 1080,
      xl: 1400,
    },
  },
  cssVariables: {
    colorSchemeSelector: '.mode-%s',
  },
  shadows: createCustomShadows(baseTheme, customElevationShadowInsets),
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: '#6F5CFF',
          dark: '#4D40B2',
          light: '#A2A4FF',
          contrastText: '#FFFFFF',
          pnl_card: '#5A43BB',
          _states: {
            hover: 'rgba(111, 92, 255, 0.04)',
            selected: 'rgba(111, 92, 255, 0.08)',
            focus: 'rgba(111, 92, 255, 0.12)',
            focusVisible: 'rgba(111, 92, 255, 0.3)',
            outlinedBorder: 'rgba(111, 92, 255, 0.5)',
          },
        },
        secondary: {
          main: '#EEEEEE',
          dark: '#BDBDBD',
          light: '#F5F5F5',
          contrastText: 'rgba(0, 0, 0, 0.8)',
          _states: {
            hover: 'rgba(238, 238, 238, 0.04)',
            selected: 'rgba(238, 238, 238, 0.08)',
            focus: 'rgba(238, 238, 238, 0.12)',
            focusVisible: 'rgba(238, 238, 238, 0.3)',
            outlinedBorder: 'rgba(238, 238, 238, 0.5)',
          },
        },
        error: {
          contrastText: '#FFFFFF',
          main: '#D32F2F',
          dark: '#C62828',
          _states: {
            focusVisible: 'rgba(211, 47, 47, 0.3)',
            outlinedBorder: 'rgba(211, 47, 47, 0.5)',
            hover: 'rgba(211, 47, 47, 0.04)',
            selected: 'rgba(211, 47, 47, 0.08)',
          },
        },
        warning: {
          contrastText: '#FFFFFF',
          main: '#EF6C00',
          dark: '#E65100',
          light: '#FF9800',
          _states: {
            focusVisible: 'rgba(239, 108, 0, 0.3)',
            outlinedBorder: 'rgba(239, 108, 0, 0.5)',
            hover: 'rgba(239, 108, 0, 0.04)',
          },
        },
        info: {
          contrastText: '#FFFFFF',
          main: '#0288D1',
          dark: '#01579B',
          _states: {
            focusVisible: 'rgba(2, 136, 209, 0.3)',
            outlinedBorder: 'rgba(2, 136, 209, 0.5)',
            hover: 'rgba(2, 136, 209, 0.04)',
          },
        },
        success: {
          contrastText: '#FFFFFF',
          main: '#2E7D32',
          dark: '#1B5E20',
          light: '#4CAF50',
          _states: {
            focusVisible: 'rgba(46, 125, 50, 0.3)',
            outlinedBorder: 'rgba(46, 125, 50, 0.5)',
            hover: 'rgba(46, 125, 50, 0.04)',
            selected: 'rgba(46, 125, 50, 0.08)',
          },
        },
        background: {
          default: '#EAEAEA',
          'paper-elevation-0': '#F1F1F1',
          'paper-elevation-1': '#F8F5FA',
          'paper-elevation-2': '#FAFAFA',
          'paper-elevation-3': '#FFFFFF',
          'paper-elevation-4': '#FFFFFF',
          'paper-elevation-5': '#FFFFFF',
          'paper-elevation-6': '#FFFFFF',
          'paper-elevation-7': '#FFFFFF',
          'paper-elevation-8': '#FFFFFF',
          surface: '#F7F7F7',
        },
        action: {
          disabled: 'rgba(0, 0, 0, 0.38)',
          disabledBackground: 'rgba(0, 0, 0, 0.12)',
          hover: 'rgba(0, 0, 0, 0.04)',
          active: 'rgba(0, 0, 0, 0.56)',
          selected: 'rgba(0, 0, 0, 0.08)',
          focus: 'rgba(0, 0, 0, 0.12)',
        },
        text: {
          primary: 'rgba(0, 0, 0, 0.87)',
          disabled: 'rgba(0, 0, 0, 0.38)',
          secondary: 'rgba(0, 0, 0, 0.60)',
          _states: {
            focusVisible: 'rgba(0, 0, 0, 0.3)',
          },
        },
        brand: {
          lavender: '#E1E1FF',
          indigo: '#A2A4FF',
          purple: '#6F5CFF',
          raisin: '#1E1A2B',
        },
        common: {
          white_states: {
            main: '#FFFFFF',
            focusVisible: 'rgba(255, 255, 255, 0.3)',
          },
          shadows: {
            perplInnerShadow: '2px 4px 4px 0px rgba(0, 0, 0, 0.20) inset',
            perplInnerOuterShadow: `
                inset 0px 2px 4px 0px rgba(230, 230, 230, 0.70),
                inset 0px 1px 2px 0px rgba(230, 230, 230, 0.50),
                0px 1px 0px 0px rgba(0, 0, 0, 0.04)
              `,
            thumbInnerShadow: `
                inset 1px 1px 1px 0px rgba(0, 0, 0, 0.24),
                inset -1px -2px 2px 0px rgba(210,210,210, 0.24),
                0px 3px 1px -2px rgba(210,210,210, 0.20),
                0px 2px 2px 0px rgba(210,210,210, 0.14),
                0px 1px 5px 0px rgba(210,210,210, 0.12)
              `,
            perplButtonShadow: `
                0px 3px 1px -2px rgba(210,210,210, 0.20),
                0px 2px 2px 0px rgba(210,210,210, 0.14),
                0px 1px 5px 0px rgba(210,210,210, 0.12),
                inset 0px 1px 2px 0px rgba(0, 0, 0, 0.54),
                inset 0px -1px 2px 0px rgba(210,210,210, 0.64)
              `,
            // Raised key badge (KeyCap). Designed for dark only; light mirrors it until a light design exists.
            keyCap: `
                0px 1px 3px 0px rgba(0, 0, 0, 0.12),
                inset 0px 1px 3px 0px rgba(255, 255, 255, 0.12),
                inset 0px -1px 2px 0px rgba(0, 0, 0, 0.16),
                inset 0px -1px 0px 0px rgba(30, 27, 35, 0.50)
              `,
          },
        },
        elevation: {
          outlined: '#E0E0E0',
        },
        _components: {
          input: {
            standard: {
              enabledBorder: 'rgba(0, 0, 0, 0.42)',
              hoverBorder: 'rgba(0, 0, 0, 1)',
            },
            filled: {
              enabledFill: 'rgba(0, 0, 0, 0.09)',
              hoverFill: 'rgba(0, 0, 0, 0.12)',
            },
            outlined: {
              enabledBorder: 'rgba(0, 0, 0, 0.08)',
              hoverBorder: 'rgba(0, 0, 0, 1)',
              gradientBorder: createGradientBorder('10%', '100%', 0.12, 'light'),
            },
          },
          rating: {
            enabledBorder: 'rgba(0, 0, 0, 0.23)',
            activeFill: '#FFB400',
          },
          tooltip: {
            background: '#636363',
          },
          alert: {
            info: {
              background: '#e5f6fd',
              color: '#014361',
            },
            warning: {
              background: '#fff4e5',
              color: '#663c00',
            },
            error: {
              background: '#fdeded',
              color: '#5f2120',
            },
            success: {
              background: '#edf7ed',
              color: '#1e4620',
            },
          },
        },
      },
      opacity: {
        orderBookProgressBar: 0.08,
      },
    },
    dark: {
      palette: {
        primary: {
          main: '#A2A4FF',
          dark: '#6366FE',
          light: '#E1E1FF',
          contrastText: 'rgba(0, 0, 0, 0.87)',
          pnl_card: '#5A43BB',
          _states: {
            hover: 'rgba(162, 164, 255, 0.08)',
            selected: 'rgba(162, 164, 255, 0.16)',
            focus: 'rgba(162, 164, 255, 0.12)',
            focusVisible: 'rgba(162, 164, 255, 0.3)',
            outlinedBorder: 'rgba(162, 164, 255, 0.5)',
          },
        },
        // Quiet-neutral surface button, matching the Master Library <Button> (A-2086).
        secondary: {
          main: '#24222A',
          dark: '#211E27',
          light: '#2A282C',
          contrastText: 'rgba(224, 225, 255, 0.7)',
          _states: {
            hover: 'rgba(36, 34, 42, 0.08)',
            selected: 'rgba(36, 34, 42, 0.16)',
            focus: 'rgba(36, 34, 42, 0.12)',
            focusVisible: 'rgba(36, 34, 42, 0.3)',
            outlinedBorder: 'rgba(36, 34, 42, 0.5)',
          },
        },
        error: {
          contrastText: 'rgba(0, 0, 0, 0.87)',
          main: '#F65A6E',
          dark: '#F10033',
          light: '#F44336',
          _states: {
            focusVisible: 'rgba(246, 90, 110, 0.3)', // #F65A6E
            outlinedBorder: 'rgba(246, 90, 110, 0.5)',
            hover: 'rgba(246, 90, 110, 0.08)',
            selected: 'rgba(246, 90, 110, 0.16)',
          },
        },
        warning: {
          contrastText: 'rgba(0, 0, 0, 0.87)',
          main: '#FFA726',
          dark: '#F57C00',
          light: '#FFB74D',
          _states: {
            focusVisible: 'rgba(255, 167, 38, 0.3)',
            outlinedBorder: 'rgba(255, 167, 38, 0.5)',
            hover: 'rgba(255, 167, 38, 0.08)',
          },
        },
        info: {
          contrastText: 'rgba(0, 0, 0, 0.87)',
          main: '#29B6F6',
          dark: '#0288D1',
          _states: {
            focusVisible: 'rgba(41, 182, 246, 0.3)',
            outlinedBorder: 'rgba(41, 182, 246, 0.5)',
            hover: 'rgba(41, 182, 246, 0.08)',
          },
        },
        success: {
          contrastText: 'rgba(0, 0, 0, 0.87)',
          main: '#81C784',
          dark: '#388E3C',
          light: '#A5D6A7',
          _states: {
            focusVisible: 'rgba(129, 199, 132, 0.3)',
            outlinedBorder: 'rgba(129, 199, 132, 0.5)',
            hover: 'rgba(129, 199, 132, 0.08)',
            selected: 'rgba(129, 199, 132, 0.16)',
          },
        },
        background: {
          default: '#000000',
          'paper-elevation-0': '#100F12',
          'paper-elevation-1': '#121113',
          'paper-elevation-2': '#16141a',
          'paper-elevation-3': '#1E1B23',
          'paper-elevation-4': '#24222A',
          'paper-elevation-5': '#2A282C',
          'paper-elevation-6': '#2C2B2D',
          'paper-elevation-7': '#2D2D2D',
          'paper-elevation-8': '#2E2E2E',
          surface: '#0c0b0e',
        },
        action: {
          disabled: 'rgba(255, 255, 255, 0.38)',
          disabledBackground: 'rgba(255, 255, 255, 0.12)',
          hover: 'rgba(255, 255, 255, 0.08)',
          active: 'rgba(255, 255, 255, 0.72)',
          selected: 'rgba(255, 255, 255, 0.16)',
          focus: 'rgba(255, 255, 255, 0.12)',
        },
        text: {
          primary: '#FFFFFF',
          disabled: 'rgba(255, 255, 255, 0.38)',
          secondary: 'rgba(224, 225, 255, 0.7)',
          _states: {
            focusVisible: 'rgba(255, 255, 255, 0.3)',
          },
        },
        brand: {
          lavender: '#E1E1FF',
          indigo: '#A2A4FF',
          purple: '#6F5CFF',
          raisin: '#1E1A2B',
        },
        common: {
          white_states: {
            main: '#FFFFFF',
            focusVisible: 'rgba(255, 255, 255, 0.3)',
          },
          shadows: {
            perplInnerShadow: '2px 4px 4px 0px rgba(0, 0, 0, 0.20) inset',
            perplInnerOuterShadow:
              'inset 0px 2px 4px rgba(0,0,0,0.70), inset 0px 1px 2px rgba(0,0,0,0.50), 0px 1px 0px rgba(255,255,255,0.04)',
            thumbInnerShadow: `
                inset 1px 1px 1px 0px rgba(255, 255, 255, 0.14),
                inset -1px -2px 2px 0px rgba(0, 0, 0, 0.24),
                0px 3px 1px -2px rgba(0, 0, 0, 0.20),
                0px 2px 2px 0px rgba(0, 0, 0, 0.14),
                0px 1px 5px 0px rgba(0, 0, 0, 0.12)
              `,
            perplButtonShadow: `
                0px 3px 1px -2px rgba(0, 0, 0, 0.20),
                0px 2px 2px 0px rgba(0, 0, 0, 0.14),
                0px 1px 5px 0px rgba(0, 0, 0, 0.12),
                inset 0px 1px 2px 0px rgba(255, 255, 255, 0.64),
                inset 0px -1px 2px 0px rgba(0, 0, 0, 0.64)
              `,
            // Raised key badge (KeyCap). Figma: drop 0 1 3 @12%, inner 0 1 3 white @12%, inner 0 -1 2 @16%;
            // the last inset approximates Figma's 1px bottom-edge gradient stroke.
            keyCap: `
                0px 1px 3px 0px rgba(0, 0, 0, 0.12),
                inset 0px 1px 3px 0px rgba(255, 255, 255, 0.12),
                inset 0px -1px 2px 0px rgba(0, 0, 0, 0.16),
                inset 0px -1px 0px 0px rgba(30, 27, 35, 0.50)
              `,
          },
        },
        elevation: {
          outlined: 'rgba(255, 255, 255, 0.12)',
        },
        _components: {
          input: {
            standard: {
              enabledBorder: 'rgba(255, 255, 255, 0.42)',
              hoverBorder: 'rgba(255, 255, 255, 1)',
            },
            filled: {
              enabledFill: '#0C0C0E',
              hoverFill: 'rgba(255, 255, 255, 0.12)',
            },
            outlined: {
              enabledBorder: 'rgba(255, 255, 255, 0.12)',
              hoverBorder: 'rgba(255, 255, 255, 1)',
              gradientBorder: createGradientBorder('10%', '100%', 0.12, 'dark'),
            },
          },
          rating: {
            enabledBorder: 'rgba(255, 255, 255, 0.23)',
            activeFill: '#FFB400',
          },
          tooltip: {
            background: '#24222A',
          },
          alert: {
            info: {
              background: '#071318',
              color: '#b8e7fb',
            },
            warning: {
              background: '#191207',
              color: '#ffe2b7',
            },
            error: {
              background: '#160b0b',
              color: '#f4c7c7',
            },
            success: {
              background: '#0c130d',
              color: '#cce8cd',
            },
          },
        },
      },
      opacity: {
        orderBookProgressBar: 0.16,
      },
    },
  },
  // h1–h5 step up at md via `:root` variables (MuiCssBaseline), so a plain `sx` font override still wins.
  typography: {
    h1: {
      fontFamily: mainFont,
      fontWeight: 300,
      fontSize: 'var(--h1-font-size, 2.25rem)',
      lineHeight: 'var(--h1-line-height, 111.1%)',
      letterSpacing: 'var(--h1-letter-spacing, -0.5px)',
    },
    h2: {
      fontFamily: mainFont,
      fontWeight: 300,
      fontSize: 'var(--h2-font-size, 1.75rem)',
      lineHeight: 'var(--h2-line-height, 114.3%)',
      letterSpacing: 'var(--h2-letter-spacing, -0.25px)',
    },
    h3: {
      fontFamily: mainFont,
      fontWeight: 300,
      fontSize: 'var(--h3-font-size, 1.5rem)',
      lineHeight: 'var(--h3-line-height, 116.7%)',
      letterSpacing: 'var(--h3-letter-spacing, 0px)',
    },
    h4: {
      fontFamily: mainFont,
      fontWeight: 400,
      fontSize: 'var(--h4-font-size, 1.375rem)',
      lineHeight: 'var(--h4-line-height, 127.3%)',
      letterSpacing: 'var(--h4-letter-spacing, 0px)',
    },
    h5: {
      fontFamily: mainFont,
      fontWeight: 400,
      fontSize: 'var(--h5-font-size, 1.25rem)',
      lineHeight: 'var(--h5-line-height, 140%)',
    },
    // Stat value: the figure under a caption label in a stat grid. 20/28 at every width, unlike h5
    stat: {
      fontSize: '1.25rem',
      fontFamily: mainFont,
      fontWeight: 400,
      lineHeight: '140%',
    },
    h6: {
      fontSize: '1rem',
      fontFamily: mainFont,
      fontWeight: 600,
      lineHeight: '125%',
    },
    body1,
    body2,
    // Emphasis keeps the body line heights so it sits in body1/body2 rows; don't use subtitle2 for it
    body1Semibold: { ...body1, fontWeight: 600 },
    body2Semibold: { ...body2, fontWeight: 600 },
    numeric: {
      fontVariantNumeric: 'tabular-nums',
      fontFamily: numericsFont,
      letterSpacing: 'var(--numeric-letter-spacing)',
    },
    subtitle1: {
      fontFamily: mainFont,
      lineHeight: '150%',
    },
    subtitle2: {
      fontFamily: mainFont,
    },
    overline: {
      fontFamily: mainFont,
      fontWeight: 500,
      fontSize: '0.6875rem',
      lineHeight: '145.5%',
      letterSpacing: '0.88px',
      textTransform: 'uppercase',
    },
    caption: {
      fontFamily: mainFont,
      lineHeight: '133%',
      letterSpacing: '0.2px',
    },
    button: {
      fontFamily: mainFont,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 4,
  },
  spacing: 8,
  // components
  components: {
    MuiPaper: {
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          const elevation = ownerState.elevation ?? 1;
          const backgroundKey = `paper-elevation-${elevation}` as keyof typeof theme.vars.palette.background;

          return {
            backgroundColor: theme.vars.palette.background[backgroundKey] ?? theme.vars.palette.background.paper,
            backgroundImage: 'none',
          };
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: 'var(--mui-palette-background-paper-elevation-3)',
        },
        list: {
          paddingBottom: 0,
          paddingTop: 0,
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 2,
        position: 'fixed',
        sx: {
          backgroundColor: 'background.paper-elevation-3',
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        fullWidth: true,
        maxWidth: 'xs',
        slotProps: {
          paper: {
            elevation: 0, // drop built-in overlay
          },
        },
      },
      styleOverrides: {
        root: {
          backdropFilter: 'blur(2px)',
        },
        paper: ({ theme }) => ({
          backgroundColor: theme.vars.palette.background['paper-elevation-2'],
          boxShadow: theme.shadows[8],
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(2px)',
        },
      },
    },

    MuiDialogTitle: {
      defaultProps: {
        component: 'h5',
        variant: 'h5',
        sx: {
          mb: 1,
        },
      },
    },

    MuiDialogContent: {
      defaultProps: {
        sx: {
          pb: 1,
        },
      },
    },

    MuiDialogActions: {
      defaultProps: {
        sx: {
          p: 3,
        },
      },
    },

    MuiTypography: {
      defaultProps: {
        // MUI falls back to its own mapping for the built-in variants
        variantMapping: { body1Semibold: 'p', body2Semibold: 'p', stat: 'p' },
      },
    },
    MuiListItemText: {
      defaultProps: {
        slotProps: {
          primary: { variant: 'body1' },
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { size: 'small' },
          style: {
            fontSize: '0.75rem',
            lineHeight: '1rem',
            padding: '4px 10px',
            height: '24px',
          },
        },
        {
          props: { size: 'medium' },
          style: {
            fontSize: '0.75rem',
            lineHeight: '1.25rem',
            letterSpacing: '0.4px',
            padding: '0.25rem 1rem',
            height: '32px',
          },
        },
        {
          props: { size: 'medium', variant: 'text' },
          style: {
            padding: '0.25rem 0.5rem',
          },
        },
        {
          props: { size: 'small', variant: 'text' },
          style: {
            fontSize: '0.75rem',
            lineHeight: '1rem',
          },
        },
        {
          props: { size: 'large' },
          style: {
            fontSize: '0.875rem',
            lineHeight: '1.5rem',
            letterSpacing: '0.46px',
            padding: '8px 22px',
            height: '36px',
          },
        },
        {
          props: { size: 'large', variant: 'text' },
          style: {
            padding: '8px 11px',
          },
        },
        // secondary.main is a surface colour, so text/outlined label with contrastText (A-2086).
        {
          props: { color: 'secondary', variant: 'text' },
          style: ({ theme: innerTheme }) => ({
            color: innerTheme.vars.palette.secondary.contrastText,
            '&:hover': { backgroundColor: innerTheme.vars.palette.action.hover },
          }),
        },
        {
          props: { color: 'secondary', variant: 'outlined' },
          style: ({ theme: innerTheme }) => ({
            color: innerTheme.vars.palette.secondary.contrastText,
            borderColor: innerTheme.vars.palette.elevation.outlined,
            '&:hover': {
              borderColor: innerTheme.vars.palette.elevation.outlined,
              backgroundColor: innerTheme.vars.palette.action.hover,
            },
          }),
        },
      ],
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
        contained: {
          boxShadow: 'var(--mui-palette-common-shadows-perplButtonShadow)',
          '&:hover': {
            boxShadow: 'var(--mui-palette-common-shadows-perplButtonShadow)',
          },
          '&:active': {
            boxShadow: 'var(--mui-palette-common-shadows-perplButtonShadow)',
          },
        },
      },
    },
    MuiRating: {
      styleOverrides: {
        iconFilled: ({ theme }) => ({
          color: theme.vars.palette._components.rating.activeFill,
        }),
      },
    },
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '&.MuiSvgIcon-fontSizeXSmall': {
            fontSize: '12px',
          },
          '&.MuiSvgIcon-fontSizeSmall': {
            fontSize: '16px',
          },
          '&.MuiSvgIcon-fontSizeMedium': {
            fontSize: '20px',
          },
          '&.MuiSvgIcon-fontSizeLarge': {
            fontSize: '24px',
          },
        },
      },
    },
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          width: 34,
          height: 18,
          padding: 0,
          overflow: 'visible',
          '@media (prefers-reduced-motion: reduce)': {
            '& .MuiSwitch-switchBase, & .MuiSwitch-thumb, & .MuiSwitch-track': {
              transition: 'none',
            },
          },
        },
        // Sized to the whole track: MUI's hidden input is sized off this box, so a thumb-sized one leaves the edges dead.
        switchBase: {
          padding: 3,
          width: 18,
          height: 18,
          minWidth: 18,
          transition: 'transform 180ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: 'var(--mui-palette-primary-main)',
            '& .MuiSwitch-thumb': {
              backgroundColor: 'var(--mui-palette-primary-main)',
            },
            '& + .MuiSwitch-track': {
              backgroundColor: 'rgba(var(--mui-palette-primary-mainChannel) / 0.28)',
              borderColor: 'var(--mui-palette-elevation-outlined)',
              opacity: 1,
            },
          },
          '&.Mui-focusVisible + .MuiSwitch-track': {
            outline: '2px solid var(--mui-palette-primary-main)',
            outlineOffset: 2,
          },
          '&:hover + .MuiSwitch-track': {
            opacity: 1,
          },
          // Keep last: same specificity as the checked/hover track rules, so source order is what dims a disabled switch.
          '&.Mui-disabled': {
            opacity: 0.4,
            '& + .MuiSwitch-track': {
              opacity: 0.4,
            },
          },
        },
        thumb: {
          width: 12,
          height: 12,
          borderRadius: '1px',
          backgroundColor: 'var(--mui-palette-text-disabled)',
          boxShadow: 'none',
          transition: 'background-color 180ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        track: {
          borderRadius: '3px',
          backgroundColor: 'var(--mui-palette-background-paper-elevation-0)',
          border: '1px solid var(--mui-palette-elevation-outlined)',
          opacity: 1,
          transition: 'background-color 180ms cubic-bezier(0.4, 0, 0.2, 1), border-color 180ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 36,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          minWidth: 40,
          minHeight: 0,
          padding: '8px 8px',
          whiteSpace: 'nowrap',
          fontSize: '0.75rem',
        }),
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
        },
        label: {
          fontSize: '0.75rem',
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          backgroundColor: theme.vars.palette._components.tooltip.background,
          color: theme.vars.palette.common.white_states.main,
          boxShadow: theme.shadows[8],
          fontSize: '12px',
          lineHeight: '16px',
          fontWeight: 400,
          letterSpacing: 0.1,
          padding: `${theme.spacing(0.75)} ${theme.spacing(1.25)}`,
          // Tooltip copy uses `\n` for paragraph breaks (e.g. the Portfolio account card).
          whiteSpace: 'pre-line',
        }),
        arrow: ({ theme }) => ({
          color: theme.vars.palette._components.tooltip.background,
        }),
        popperArrow: {
          '&[data-popper-placement*="top"] .MuiTooltip-arrow': {
            marginBottom: '-6px',
          },
          '&[data-popper-placement*="bottom"] .MuiTooltip-arrow': {
            marginTop: '-6px',
          },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        standard: ({ ownerState, theme }) => {
          const severity = ownerState.severity ?? 'success';
          return {
            backgroundColor: theme.vars.palette._components.alert[severity].background,
            color: theme.vars.palette._components.alert[severity].color,
          };
        },
        outlined: ({ ownerState, theme }) => {
          const severity = ownerState.severity ?? 'success';
          return {
            color: theme.vars.palette._components.alert[severity].color,
            borderColor: theme.vars.palette[severity].main,
          };
        },
      },
    },

    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          fontWeight: 500,
          lineHeight: '133%',
          letterSpacing: '0.15px',
        },
      },
    },

    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        '@keyframes sheen': {
          '0%': { transform: 'translateX(-150%) skewX(-20deg)' },
          '10%': { transform: 'translateX(150%) skewX(-20deg)' },
          '100%': { transform: 'translateX(150%) skewX(-20deg)' },
        },

        // '*': {
        //   fontVariantNumeric: 'tabular-nums',
        // },
        ':root': {
          '--scroll-color': 'var(--mui-palette-text-secondary)',
          '--numeric-letter-spacing': '-0.5px',
          '--main-font-family': '"Geist", -apple-system, "system-ui", "Trebuchet MS", Roboto, Ubuntu, sans-serif',
          '--numeric-font-family': NUMERIC_FONT_FAMILY,
          [theme.breakpoints.up('md')]: {
            '--h1-font-size': '3.5rem',
            '--h1-line-height': '107.1%',
            '--h1-letter-spacing': '-1px',
            '--h2-font-size': '2.5rem',
            '--h2-line-height': '110%',
            '--h2-letter-spacing': '-0.5px',
            '--h3-font-size': '2rem',
            '--h3-line-height': '112.5%',
            '--h3-letter-spacing': '-0.5px',
            '--h4-font-size': '1.75rem',
            '--h4-line-height': '114.3%',
            '--h4-letter-spacing': '-0.25px',
            '--h5-font-size': '1.5rem',
            '--h5-line-height': '133.4%',
          },
        },
        body: {
          scrollbarColor: 'var(--scroll-color) transparent',
        },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: 'var(--scroll-color)',
          borderRadius: '4px',
        },
        '::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        img: {
          display: 'block',
          maxWidth: '100%',
          height: 'auto',
        },
        // Global selector for all numeric elements
        '.numeric, [data-numeric="true"]': {
          fontVariantNumeric: 'tabular-nums',
          fontFamily: 'var(--numeric-font-family)',
          letterSpacing: 'var(--numeric-letter-spacing)',
        },
        // Target specific HTML elements that typically contain numbers
        'input[type="number"], .MuiInputBase-input[type="number"], .MuiInputBase-input[inputmode="decimal"], input[inputmode="decimal"]':
          {
            fontVariantNumeric: 'tabular-nums',
            fontFamily: 'var(--numeric-font-family)',
            letterSpacing: 'var(--numeric-letter-spacing)',
          },
        // Target table cells that contain only numbers
        'td.MuiTableCell-alignRight, .numeric-cell': {
          fontVariantNumeric: 'tabular-nums',
          fontFamily: 'var(--numeric-font-family)',
          letterSpacing: 'var(--numeric-letter-spacing)',
        },
        // Apply to all elements with numeric content using an attribute selector
        '[data-content-type="numeric"]': {
          fontVariantNumeric: 'tabular-nums',
          fontFamily: 'var(--numeric-font-family)',
          letterSpacing: 'var(--numeric-letter-spacing)',
        },
      }),
    },
    // Fill and pseudo-borders stay together here — split across MuiTextField, a bare OutlinedInput got only half.
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          position: 'relative',
          boxShadow: 'var(--mui-palette-common-shadows-perplInnerOuterShadow)',
          background: theme.vars.palette.background['paper-elevation-0'],
          // Composited over the fill, not an elevation swap: dark elevation-0 and -1 are 2/255 apart.
          '&:hover:not(.Mui-disabled)': {
            backgroundImage: `linear-gradient(${theme.vars.palette.action.hover}, ${theme.vars.palette.action.hover})`,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&::before': createGradientBorderPseudo(theme.vars.palette._components.input.outlined.gradientBorder),
          '&::after': {
            ...createGradientBorderPseudo(theme.vars.palette.primary.main),
            opacity: 0,
            transition: theme.transitions.create('opacity', {
              duration: theme.transitions.duration.shorter,
              easing: theme.transitions.easing.easeOut,
            }),
          },
          '&.Mui-focused::after': {
            opacity: 1,
          },
        }),
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          height: '8px',
        },
        rail: {
          borderRadius: '2px',
          border: '1px solid var(--mui-palette-elevation-outlined, rgba(255, 255, 255, 0.12))',
          boxShadow: '2px 2px 2px 0px rgba(21, 20, 41, 0.40) inset',
        },
      },
    },
    ...MuiDataGrid,
  },
  custom: {
    helpText: {
      textDecorationLine: 'underline',
      textDecorationStyle: 'dotted',
      textDecorationSkipInk: 'true',
      textDecorationThickness: '8%',
      textUnderlineOffset: '12%',
      textUnderlinePosition: 'from-font',
      cursor: 'help',
    },
  },
});
